import { useEffect, useState } from "react"
import {
  App,
  Descriptions,
  Empty,
  Flex,
  Modal,
  Spin,
  Tag,
  Typography,
} from "antd"
import { Alert } from "~/components/Alert"
import { getStaffOrderCleanupOpDetail } from "~/api/staff"
import {
  OrderDetails,
  PaymentDetails,
  RechargeDetails,
} from "../OrderCleanup/Details"
import {
  cleanupOperationColor,
  cleanupOperationLabel,
  cleanupStatusColor,
  cleanupStatusLabel,
} from "./presentation"
import {
  formatAmount,
  formatDateTime,
  formatValue,
  isAbortError,
} from "~/common/utils"
import styles from "./index.css"

const { Text, Title } = Typography

const normalizeSnapshotId = <T extends { id?: string }>(
  snapshot: T & { _id?: string },
): T => {
  const mongoId = snapshot["_id"]
  if (snapshot.id || !mongoId) return snapshot

  return { ...snapshot, id: mongoId }
}

const normalizeDetailSnapshotIds = (
  detail: API.Staff.StaffOrderCleanupOpLogDetailResponse,
): API.Staff.StaffOrderCleanupOpLogDetailResponse => {
  const opLog = detail.op_log

  return {
    op_log: {
      ...opLog,
      order_snapshot: opLog.order_snapshot
        ? normalizeSnapshotId(opLog.order_snapshot)
        : null,
      payment_trade_snapshot: opLog.payment_trade_snapshot
        ? normalizeSnapshotId(opLog.payment_trade_snapshot)
        : null,
      wallet_recharge_record_snapshots:
        opLog.wallet_recharge_record_snapshots.map(normalizeSnapshotId),
    },
  }
}

export const CleanupLogDetailModal = ({
  opLogId,
  onClose,
}: {
  opLogId: string | null
  onClose: () => void
}) => {
  const { message } = App.useApp()
  const [detail, setDetail] =
    useState<API.Staff.StaffOrderCleanupOpLogDetailResponse | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!opLogId) {
      setDetail(null)
      return
    }

    const controller = new AbortController()

    const loadDetail = async () => {
      setLoading(true)
      setDetail(null)

      try {
        const response = await getStaffOrderCleanupOpDetail(
          { op_log_id: opLogId },
          controller.signal,
        )
        if (controller.signal.aborted) return

        if (!response.data) {
          void message.warning("高危操作详情为空")
          return
        }
        setDetail(normalizeDetailSnapshotIds(response.data))
      } catch (error) {
        if (!isAbortError(error)) {
          setDetail(null)
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }

    void loadDetail()
    return () => controller.abort()
  }, [message, opLogId])

  const opLog = detail?.op_log
  const hasSnapshots = Boolean(
    opLog?.order_snapshot ||
    opLog?.payment_trade_snapshot ||
    opLog?.wallet_recharge_record_snapshots.length,
  )

  return (
    <Modal
      destroyOnHidden
      footer={null}
      open={Boolean(opLogId)}
      title="危险操作记录详情"
      width={1180}
      onCancel={onClose}
    >
      <Spin spinning={loading}>
        {opLog ? (
          <Flex
            className={styles.detailContent}
            gap={20}
            vertical
          >
            {opLog.status === "FAILED" ? (
              <Alert
                description={opLog.error_message || "未记录具体失败原因"}
                showIcon
                title="本次高危操作执行失败"
                level="strong"
              />
            ) : null}

            <section>
              <Title level={5}>操作审计信息</Title>
              <Descriptions
                bordered
                column={3}
                size="small"
              >
                <Descriptions.Item label="记录ID">
                  {formatValue(opLog.id)}
                </Descriptions.Item>
                <Descriptions.Item label="操作类型">
                  <Tag color={cleanupOperationColor[opLog.operation]}>
                    {cleanupOperationLabel[opLog.operation]}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="操作状态">
                  <Tag color={cleanupStatusColor[opLog.status]}>
                    {cleanupStatusLabel[opLog.status]}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="订单号">
                  {opLog.order_no}
                </Descriptions.Item>
                <Descriptions.Item label="订单ID">
                  {formatValue(opLog.order_id)}
                </Descriptions.Item>
                <Descriptions.Item label="支付记录ID">
                  {formatValue(opLog.payment_trade_id)}
                </Descriptions.Item>
                <Descriptions.Item label="订单用户ID">
                  {formatValue(opLog.user_id)}
                </Descriptions.Item>
                <Descriptions.Item label="订单金额">
                  {formatAmount(opLog.amount)}
                </Descriptions.Item>
                <Descriptions.Item label="订单类型">
                  {formatValue(opLog.order_type)}
                </Descriptions.Item>
                <Descriptions.Item label="操作Staff手机号">
                  {opLog.staff_mobile}
                </Descriptions.Item>
                <Descriptions.Item label="操作Staff ID">
                  {opLog.staff_id}
                </Descriptions.Item>
                <Descriptions.Item label="创建时间">
                  {formatDateTime(opLog.created_t)}
                </Descriptions.Item>
                <Descriptions.Item label="更新时间">
                  {formatDateTime(opLog.modified_t)}
                </Descriptions.Item>
                <Descriptions.Item label="确认退款时间">
                  {formatDateTime(opLog.confirmed_at)}
                </Descriptions.Item>
                <Descriptions.Item label="确认人手机号">
                  {formatValue(opLog.confirmed_by_staff_mobile)}
                </Descriptions.Item>
                <Descriptions.Item label="确认人Staff ID">
                  {formatValue(opLog.confirmed_by_staff_id)}
                </Descriptions.Item>
                <Descriptions.Item label="错误码">
                  {formatValue(opLog.error_code)}
                </Descriptions.Item>
                <Descriptions.Item label="异常类型">
                  {formatValue(opLog.error_type)}
                </Descriptions.Item>
                <Descriptions.Item
                  label="错误信息"
                  span={3}
                >
                  {opLog.error_message ? (
                    <Text type="danger">{opLog.error_message}</Text>
                  ) : (
                    "-"
                  )}
                </Descriptions.Item>
              </Descriptions>
            </section>

            {opLog.result ? (
              <section>
                <Title level={5}>操作结果</Title>
                <pre className={styles.resultJson}>
                  {JSON.stringify(opLog.result, null, 2)}
                </pre>
              </section>
            ) : null}

            {opLog.order_snapshot ? (
              <OrderDetails order={opLog.order_snapshot} />
            ) : null}
            {opLog.payment_trade_snapshot ? (
              <PaymentDetails payment={opLog.payment_trade_snapshot} />
            ) : null}
            {opLog.wallet_recharge_record_snapshots.length ? (
              <RechargeDetails
                records={opLog.wallet_recharge_record_snapshots}
              />
            ) : null}
            {!hasSnapshots ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="该操作记录没有保存业务数据快照"
              />
            ) : null}
          </Flex>
        ) : (
          <Empty
            className={styles.detailEmpty}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={loading ? "正在加载操作详情" : "未加载到操作详情"}
          />
        )}
      </Spin>
    </Modal>
  )
}
